import { z } from 'zod';
import { API_ENDPOINTS } from '@/config/apiEndpoints';
import { apiClient } from '@/shared/utils/api/apiClient';
import { parseOrThrow } from '@/shared/utils/api/parseResponse';
import { snapshotMetadataSchema, toSnapshotMetadata } from '@/features/snapshots/api/snapshotContracts';
import type { SnapshotMetadata } from '@/features/snapshots/types/snapshot.types';
import type { ModelId, Paginated } from '@/shared/types/common.types';
import {
  NO_SYNC_FACTS,
  projectFamiliesResponseSchema,
  projectFamilyCountSchema,
  projectListResponseSchema,
  projectResponseSchema,
  projectSheetSearchResponseSchema,
  toFamilyInstance,
  toProjectFamily,
  toProjectSheet,
  toProjectSummary,
} from '../api/projectContracts';
import type { ModelSyncFacts } from '../api/projectContracts';
import type { ProjectDetail, ProjectQuery, ProjectSheet, ProjectSummary } from '../types/project.types';

/**
 * `RevitProjectSpecParams` caps a page at 50. `pageSize=0` is not "everything":
 * pagination stays on and the specification applies `Take(0)`, so the response is empty.
 */
const PROJECT_FAMILY_PAGE_SIZE = 50;

/** `RevitProjectContentSearch` clamps a page to 100. */
const PROJECT_SHEET_PAGE_SIZE = 100;

/**
 * Reduces a model's snapshots to the synchronisation facts the project list shows.
 *
 * The newest snapshot is authoritative for version, size and author, because it is the one
 * that produced the model as it stands. Its `lastSynchronized` can be null on an older
 * record, so the most recent non-null across the set is used instead.
 */
function summariseSync(snapshots: readonly SnapshotMetadata[]): ModelSyncFacts {
  if (snapshots.length === 0) return NO_SYNC_FACTS;

  const newest = snapshots.reduce((latest, candidate) =>
    candidate.modelVersion > latest.modelVersion ? candidate : latest
  );

  const synchronised = snapshots
    .map((snapshot) => snapshot.lastSynchronized ?? snapshot.createdAt)
    .filter((value): value is string => value !== null)
    .sort();

  return {
    modelVersion: newest.modelVersion,
    lastSynchronized: synchronised.at(-1) ?? null,
    snapshotCount: snapshots.length,
    fileSizeBytes: newest.fileSizeBytes,
    authorFullName: newest.authorFullName.length > 0 ? newest.authorFullName : null,
  };
}

function groupSnapshotsByModel(
  snapshots: readonly SnapshotMetadata[]
): Map<number, SnapshotMetadata[]> {
  const grouped = new Map<number, SnapshotMetadata[]>();

  for (const snapshot of snapshots) {
    const key = Number(snapshot.modelId);
    const existing = grouped.get(key);
    if (existing === undefined) grouped.set(key, [snapshot]);
    else existing.push(snapshot);
  }

  return grouped;
}

async function fetchProjectFamilies(modelId: ModelId): Promise<ReturnType<typeof toProjectFamily>[]> {
  const countPayload = await apiClient.get<unknown>(API_ENDPOINTS.families.projectFamilies(modelId), {
    params: { pageIndex: 0, pageSize: 1, isCount: true },
  });
  const total = parseOrThrow(projectFamilyCountSchema, countPayload, 'project family count').count;
  if (total === 0) return [];

  const pageCount = Math.ceil(total / PROJECT_FAMILY_PAGE_SIZE);
  const pages = await Promise.all(
    Array.from({ length: pageCount }, (_, pageIndex) =>
      apiClient
        .get<unknown>(API_ENDPOINTS.families.projectFamilies(modelId), {
          params: { pageIndex, pageSize: PROJECT_FAMILY_PAGE_SIZE, isCount: false },
        })
        .then(
          (payload) =>
            parseOrThrow(projectFamiliesResponseSchema, payload, 'project families').families.map(
              toProjectFamily
            )
        )
    )
  );

  return pages.flat();
}

class ProjectService {
  /**
   * Lists stored Revit project documents, with their synchronisation history attached.
   *
   * Two requests rather than one, because no single endpoint has both halves.
   * `RevitFileProject/GetAll` returns the documents but its protobuf mapper omits
   * `ModelVersion`, `LastSynchronized` and any author, so the versions come from
   * `SnapshotMetaData/GetAll` and are matched on `RevitModelId`. Both are unpaginated by
   * the server — `GetAll` takes no page parameters at all — so searching, filtering and
   * paging happen here.
   */
  async list(query: ProjectQuery): Promise<Paginated<ProjectSummary>> {
    const [projectsPayload, snapshotsPayload] = await Promise.all([
      apiClient.get<unknown>(API_ENDPOINTS.projects.list),
      apiClient.get<unknown>(API_ENDPOINTS.snapshots.list),
    ]);

    const documents = parseOrThrow(
      projectListResponseSchema,
      projectsPayload,
      'project list'
    ).projects;

    const snapshots = parseOrThrow(
      z.array(snapshotMetadataSchema),
      snapshotsPayload,
      'snapshot list'
    ).map(toSnapshotMetadata);

    const byModel = groupSnapshotsByModel(snapshots);

    const all = documents.map((document) =>
      toProjectSummary(document, summariseSync(byModel.get(document.id) ?? []))
    );

    const term = query.search?.trim().toLowerCase() ?? '';
    const relationTypes = query.relationTypes;

    const matched = all
      .filter((project) => {
        if (
          term.length > 0 &&
          !`${project.name} ${project.title} ${project.path}`.toLowerCase().includes(term)
        ) {
          return false;
        }

        if (relationTypes !== undefined && relationTypes.length > 0) {
          return relationTypes.includes(project.relationType);
        }

        return true;
      })
      .sort((a, b) => a.name.localeCompare(b.name));

    const start = query.page * query.pageSize;

    return {
      items: matched.slice(start, start + query.pageSize),
      totalCount: matched.length,
      page: query.page,
      pageSize: query.pageSize,
    };
  }

  /** One document, with its placed families, loaded project families, and snapshot history. */
  async getById(modelId: ModelId): Promise<ProjectDetail> {
    const [projectPayload, snapshots, familiesPayload] = await Promise.all([
      apiClient.get<unknown>(API_ENDPOINTS.projects.getById(modelId)),
      apiClient
        .get<unknown>(API_ENDPOINTS.snapshots.byModel(modelId))
        .then((payload) =>
          parseOrThrow(z.array(snapshotMetadataSchema), payload, 'snapshot list').map(
            toSnapshotMetadata
          )
        ),
      fetchProjectFamilies(modelId),
    ]);

    const document = parseOrThrow(projectResponseSchema, projectPayload, 'project').project;

    return {
      ...toProjectSummary(document, summariseSync(snapshots)),
      familyInstances: document.familyInstances.map(toFamilyInstance),
      projectFamilies: familiesPayload,
    };
  }

  /**
   * Every project the user can reach, for pickers that need to span them.
   *
   * The snapshot count is what tells a picker whether a model has enough history to
   * compare, so the same two-request composition as `list` is used rather than the bare
   * document list.
   */
  async listAll(): Promise<readonly ProjectSummary[]> {
    const page = await this.list({ page: 0, pageSize: Number.MAX_SAFE_INTEGER });
    return page.items;
  }

  /**
   * Sheets belonging to one model.
   *
   * The same query the desktop project browser runs when its element list is set to
   * Sheets: `POST RevitProjectContentSearch/Query` with `elementTypes: ["Sheet"]` and
   * this model's id. There is no sheet `GetById`.
   */
  async listSheets(modelId: ModelId): Promise<readonly ProjectSheet[]> {
    const sheets: ProjectSheet[] = [];
    let pageIndex = 0;
    let total = Number.POSITIVE_INFINITY;

    while (sheets.length < total) {
      const payload = await apiClient.post<unknown>(API_ENDPOINTS.families.search, {
        elementTypes: ['Sheet'],
        projectIds: [Number(modelId)],
        includeFacets: false,
        pageIndex,
        pageSize: PROJECT_SHEET_PAGE_SIZE,
      });
      const page = parseOrThrow(projectSheetSearchResponseSchema, payload, 'project sheets');
      total = page.totalCount;
      sheets.push(...page.results.map(toProjectSheet));
      pageIndex += 1;
      if (page.results.length === 0) break;
    }

    return sheets;
  }
}

export const projectService = new ProjectService();
